import { useCallback, useEffect, useState } from "react";
import { Table, Spin, Row, Col } from "antd";
import {
  fetchStudentTable,
  filterTable,
  sortTable,
  searchTable
} from "redux/reducers/StudentsTableSlice";
import { useAppSelector, useAppDispatch } from "redux/hooks";
import { CSVLink } from "react-csv";
import ErrorToDisplay from "components/error-handling/ErrorToDisplay";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { TABLE_TYPE, FILTER_LABEL, TABLE_FILTER } from "utils/Constants";
import { ColumnGroupType, ColumnType, TablePaginationConfig } from "antd/lib/table";
import iconSearch from "assets/icons/icon-search.svg";
import Search from "antd/lib/input/Search";
import { FilterValue } from "antd/lib/table/interface";
import emptyBox from "assets/images/empty-box.svg";
import { appliedAdmOpt, schoolRatedOpt, srsLodgedOpt, studentTableSize } from "utils/AssetsData";
import TableFooter from "components/common/table-footer";
import { PaginationPropType } from "utils/Types";
import {
  getStatusAndStyling,
  getTextForShowTotal,
  hasFilterChange,
  updateFilters
} from "utils/hooks/studentTableActions";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { isEmpty } from "lodash";
import useBreakpoints from "utils/hooks/useBreakpoints";
import { AccessibleSortMsg } from "components/common/accessible-sort-msg";
import styles from "./style.module.css";
import { formatName } from "utils/Format";
import FilterBox from "components/common/filterBox";
import { StudentTableType } from "redux/initialization/StudentTableInit";
import TableTitle from "components/common/table-title/title";

export const studentTableBreakpoints = {
  xs: {
    width: false
  },
  md: {
    width: true
  }
};

const Students: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const role = TABLE_TYPE.STUDENTS;
  const {
    STUDENTS,
    isLoading: isStudentTableDataLoading,
    isError: isStudentTableError
  } = useAppSelector(state => state.students);
  const { env } = useAppSelector(state => state.globalUI);
  const {
    tableData,
    tableDataForCSV,
    loading: tableLoading,
    currentPage,
    paginatedTableData,
    filters
  } = STUDENTS;
  const isStudentEmpty = tableData.length == 0;
  const [admFilter, setAdmFilter] = useState([]);
  const [srsFilter, setSrsFilter] = useState([]);
  const [statusFilter, setStatusFilter] = useState([]);

  const pagination = {
    defaultCurrent: 1,
    total: tableData?.length,
    pageSize: studentTableSize,
    current: currentPage,
    responsive: true,
    showTotal: (total: any, range: any) => getTextForShowTotal(range[0], range[1], total)
  } as PaginationPropType;

  const showTableFooter = () => <TableFooter paginationProps={pagination} tableType={role} />;

  useEffect(() => {
    window.scrollTo(0, 0);
    document.getElementById("skip-main-wrap")?.focus();
  }, []);

  useEffect(() => {
    dispatch(fetchStudentTable({ source: env }));
  }, [env]);

  const { mappedValue: mappedWidths } = useBreakpoints(studentTableBreakpoints);

  const searchByNameOrStudentId = useCallback((val: string): void => {
    const upperCaseVal = val?.toUpperCase()?.trim();
    dispatch(searchTable({ target: upperCaseVal, role }));
  }, []);

  const handleChange = useCallback(
    (
      pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: any
    ) => {
      if (Object.prototype.hasOwnProperty.call(sorter, "column")) {
        const sortDir =
          sorter.order == "ascend" ? "asc" : sorter.order == "descend" ? "desc" : undefined;
        const params = { field: sorter.field, dir: sortDir ?? undefined };

        if (!isEmpty(params)) {
          dispatch(sortTable({ sortObj: params, role }));
        }
      }
    },
    []
  );

  const applyOnClose = (visible: boolean, type: string, closeFilter?: any) => {
    if (!visible) {
      onFilter(type, closeFilter);
    }
  };

  const clearFilter = useCallback((type: string) => {
    if (type === TABLE_FILTER.ADMIN_LODGED) {
      setAdmFilter([]);
    } else if (type === TABLE_FILTER.SRS_LODGED) {
      setSrsFilter([]);
    } else if (type === TABLE_FILTER.SCHOOL_RATED) {
      setStatusFilter([]);
    }
  }, []);

  const onFilter = (type: string, closeFilter?: any) => {
    if (type === TABLE_FILTER.ADMIN_LODGED) {
      const newFilters = updateFilters(filters, admFilter, type);
      if (hasFilterChange(filters, newFilters)) {
        dispatch(filterTable({ newFilters, role }));
      }
    } else if (type === TABLE_FILTER.SRS_LODGED) {
      const newFilters = updateFilters(filters, srsFilter, type);
      if (hasFilterChange(filters, newFilters)) {
        dispatch(filterTable({ newFilters, role }));
      }
    } else if (type === TABLE_FILTER.SCHOOL_RATED) {
      const newFilters = updateFilters(filters, statusFilter, type);
      if (hasFilterChange(filters, newFilters)) {
        dispatch(filterTable({ newFilters, role }));
      }
    }

    closeFilter && closeFilter();
  };

  const studentTableColumnsInAdmin: any | ColumnGroupType<any> | ColumnType<any> = [
    {
      key: "givenName1",
      dataIndex: "givenName1",
      title: ({ sortOrder }: any) => {
        return (
          <span>
            First name <AccessibleSortMsg sortOrder={sortOrder} />
          </span>
        );
      },
      csvTitle: "First name",
      fixed: mappedWidths?.width,
      sorter: (a: any, b: any) => a.givenName1.localeCompare(b.givenName1),
      render: formatName,
      showSorterTooltip: true
    },
    {
      key: "familyName",
      dataIndex: "familyName",
      title: ({ sortOrder }: any) => {
        return (
          <span>
            Family name <AccessibleSortMsg sortOrder={sortOrder} />
          </span>
        );
      },
      csvTitle: "Family name",
      fixed: mappedWidths?.width,
      sorter: (a: any, b: any) => a.familyName.localeCompare(b.familyName),
      render: formatName,
      showSorterTooltip: true
    },
    {
      key: "studentNumber",
      dataIndex: "studentNumber",
      title: ({ sortOrder }: any) => (
        <TableTitle
          sortOrder={sortOrder}
          title={t("students.studentIDTitle")}
          tooltipTitle={t("students.studentID")}
          altText={t("students.studentID")}
        />
      ),
      csvTitle: t("students.studentIDTitle"),
      sorter: (a: any, b: any) => a.studentNumber.localeCompare(b.studentNumber),
      showSorterTooltip: false
    },
    {
      key: "dob",
      dataIndex: "dob",
      title: "Date of birth",
      csvTitle: "Date of Birth"
    },
    {
      key: "appliedAdmission",
      dataIndex: "appliedAdmission",
      title: (
        <TableTitle
          title={t("students.uacApplicationTitle")}
          tooltipTitle={t("students.uacApplication")}
          altText={t("students.uacApplication")}
        />
      ),
      csvTitle: t("students.uacApplicationTitle"),
      filterDropdown: ({ close: closeFilter }: any) => (
        <FilterBox
          option={appliedAdmOpt}
          handleChange={(checked: any) => setAdmFilter(checked)}
          values={admFilter}
          clearFilter={() => clearFilter(TABLE_FILTER.ADMIN_LODGED)}
          onFilter={() => onFilter(TABLE_FILTER.ADMIN_LODGED, closeFilter)}
        />
      ),
      onFilterDropdownOpenChange: (visible: any) =>
        applyOnClose(visible, TABLE_FILTER.ADMIN_LODGED),
      render: (_: any, { appliedAdmission }: any): JSX.Element => {
        return (
          <div>
            {appliedAdmission ? (
              <>
                <CheckOutlined className={styles["right-space"]} alt="" aria-hidden={true} />
                {FILTER_LABEL.YES}
              </>
            ) : (
              <>
                <CloseOutlined className={styles["right-space"]} alt="" aria-hidden={true} />
                {FILTER_LABEL.NO}
              </>
            )}
          </div>
        );
      }
    },
    {
      key: TABLE_FILTER.SRS_LODGED,
      dataIndex: TABLE_FILTER.SRS_LODGED,
      title: (
        <TableTitle
          title={t("students.SRSTitle")}
          tooltipTitle={t("students.SRS")}
          altText={t("students.SRS")}
        />
      ),
      csvTitle: t("students.SRSTitle"),
      filterDropdown: ({ close: closeFilter }: any) => (
        <FilterBox
          option={srsLodgedOpt}
          handleChange={(checked: any) => setSrsFilter(checked)}
          values={srsFilter}
          clearFilter={() => clearFilter(TABLE_FILTER.SRS_LODGED)}
          onFilter={() => onFilter(TABLE_FILTER.SRS_LODGED, closeFilter)}
        />
      ),
      onFilterDropdownOpenChange: (visible: any) => applyOnClose(visible, TABLE_FILTER.SRS_LODGED),
      render: (_: any, { appliedAdmission, srsLodged }: any): JSX.Element => {
        return appliedAdmission ? (
          <div className={srsLodged ? "applied-tag" : "no-applied-tag"}>
            {srsLodged ? FILTER_LABEL.YES : FILTER_LABEL.NO}
          </div>
        ) : (
          <></>
        );
      }
    },
    {
      key: TABLE_FILTER.SCHOOL_RATED,
      dataIndex: TABLE_FILTER.SCHOOL_RATED,
      title: (
        <TableTitle
          title={t("students.statusTitle")}
          tooltipTitle={t("students.status")}
          altText={t("students.status")}
        />
      ),
      csvTitle: t("students.statusTitle"),
      filterDropdown: ({ close: closeFilter }: any) => (
        <FilterBox
          option={schoolRatedOpt}
          handleChange={(checked: any) => setStatusFilter(checked)}
          values={statusFilter}
          clearFilter={() => clearFilter(TABLE_FILTER.SCHOOL_RATED)}
          onFilter={() => onFilter(TABLE_FILTER.SCHOOL_RATED, closeFilter)}
        />
      ),
      onFilterDropdownOpenChange: (visible: any) =>
        applyOnClose(visible, TABLE_FILTER.SCHOOL_RATED),
      render: (_: any, { appliedAdmission, srsLodged, schoolRated }: any): JSX.Element => {
        const isValid = appliedAdmission && srsLodged;
        const obj = getStatusAndStyling(schoolRated);
        return <div className={isValid ? obj.cssClass : ""}>{isValid ? obj.label : ""}</div>;
      }
    }
  ];

  const csvHeaders = studentTableColumnsInAdmin.map((col: any) => {
    return {
      label: col.csvTitle,
      key: col.key
    };
  });

  return (
    <>
      <Helmet>
        <title>{t("helmet.students.title")}</title>
        <meta name="description" content={t("helmet.students.meta") || ""} />
      </Helmet>

      {isStudentTableError ? (
        <ErrorToDisplay title={t("loadingError")} />
      ) : isStudentTableDataLoading ? (
        <Spin size="large" className="center-content" style={{ height: "50vh" }} />
      ) : (
        <div id="admin-student-list">
          <Row>
            <Col span={20}>
              <h1 className="display-mid-typo gap-section">{t("students.title")}</h1>
            </Col>
            <Col span={4}>
              <CSVLink
                data={tableDataForCSV ?? []}
                headers={csvHeaders}
                filename={"Students_Table.csv"}
                aria-label="Export as CSV file for students information"
                className={["ant-btn ant-btn-primary", styles["csv-btn"]].join(" ")}
              >
                {t("students.csvBtn")}
              </CSVLink>
            </Col>
          </Row>
          <Search
            placeholder="Search by name or ID"
            onSearch={searchByNameOrStudentId}
            className={styles["searchBox"]}
            aria-label="Search box for students table data"
            enterButton={<img src={iconSearch} alt="" />}
          />
          <Table
            rowKey={(record: StudentTableType) => `${record?.refNum}${record?.studentNumber}`}
            className="student-table"
            columns={studentTableColumnsInAdmin}
            locale={{
              emptyText: (
                <span>
                  <img src={emptyBox} alt="" />
                  <p>{t("students.empty")}</p>
                </span>
              )
            }}
            dataSource={paginatedTableData}
            footer={isStudentEmpty ? undefined : showTableFooter}
            pagination={false}
            loading={tableLoading}
            onChange={handleChange}
            scroll={{ x: 992 }}
          />
        </div>
      )}
    </>
  );
};

export default Students;
